import { FC, useEffect, useRef, useState } from "react";
import cn from "classnames";
import { CornerLabel } from "@datassential/platform-ui-lib";
import SectionHeader from "@components/new/SectionHeader/SectionHeader";
import Button from "@components/new/Button/Button";
import Loader from "@components/new/Loader/Loader";
import useQueryParam from "@core/hooks/useQueryParam";
import { useSalesStrategy } from "@apiHooks/useSalesStrategy";
import StrategyContent from "./StrategyContent/StrategyContent";
import StrategyCopyButton from "./StrategyCopyButton/StrategyCopyButton";
import StrategyGetStartedForm from "./StrategyGetStartedForm/StrategyGetStartedForm";
import StrategyTitleInfo from "./StrategyTitleInfo/StrategyTitleInfo";
import StrategyViewToggle from "./StrategyViewToggle/StrategyViewToggle";
import { useStrategyGeneration } from "./useStrategyGeneration";
import { getStrategyContentSections, getStrategyDisplayState, isStrategyIncomplete } from "./Strategy.helpers";
import {
    SSB_ALMOST_THERE_TEXT,
    SSB_BETA_LABEL,
    SSB_GENERATING_TEXT,
    SSB_RESTART,
    SSB_TITLE,
    StrategyDisplayStateEnum,
} from "./Strategy.constants";
import "./Strategy.styles.scss";

const CHAIN_HEADER_COMPACT_THRESHOLD_PX = 150;
const SVG_TITLE_HEIGHT_PX = 48;

const Strategy: FC = () => {
    const chainId = useQueryParam("id");
    const initial = useSalesStrategy(chainId);
    const generation = useStrategyGeneration(chainId);
    const hasResumedRef = useRef(false);
    const rootRef = useRef<HTMLDivElement>(null);

    const view = generation.data ?? initial.data;
    const { state, isPartial, sections, userInput, createdAt } = view;
    const hadGenerationSectionsRef = useRef(false);
    const hasGenerationSections = (generation.data?.sections.length ?? 0) > 0;

    const displayState = getStrategyDisplayState(
        initial.isLoading,
        state,
        generation.isGenerating,
        !!generation.errorMessage,
        sections.length > 0,
    );
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isHeaderCompact, setIsHeaderCompact] = useState(false);
    const { canExpand } = getStrategyContentSections(sections);
    const { resumePolling } = generation;

    const handleSubmit = (value: string | null) => {
        setIsFormOpen(false);
        generation.generate(value);
    };

    useEffect(() => {
        if (hasResumedRef.current) return;
        if (initial.isLoading || initial.data.state !== 'InProgress') return;

        hasResumedRef.current = true;
        resumePolling();
    }, [initial.isLoading, initial.data.state, resumePolling]);

    useEffect(() => {
        if (hasGenerationSections === hadGenerationSectionsRef.current) return;
        hadGenerationSectionsRef.current = hasGenerationSections;
        if (hasGenerationSections) setIsExpanded(true);
    }, [hasGenerationSections]);

    useEffect(() => {
        const chainHeaderEl = document.querySelector<HTMLElement>('.ChainPage__header');
        const rootEl = rootRef.current;
        if (!chainHeaderEl || !rootEl) return undefined;

        const updateStickyTop = () => {
            const chainHeaderTop = parseFloat(getComputedStyle(chainHeaderEl).top) || 0;
            const chainHeaderHeight = chainHeaderEl.offsetHeight;
            rootEl.style.setProperty('--strategy-sticky-top', `${chainHeaderTop + chainHeaderHeight}px`);
            setIsHeaderCompact(chainHeaderHeight < CHAIN_HEADER_COMPACT_THRESHOLD_PX);
        };

        updateStickyTop();
        const observer = new ResizeObserver(updateStickyTop);
        observer.observe(chainHeaderEl);

        return () => observer.disconnect();
    }, []);

    const isTitleCompact = isHeaderCompact && isExpanded;

    useEffect(() => {
        const rootEl = rootRef.current;
        if (!rootEl) return undefined;

        const measureTitleWidth = () => {
            const svgEl = rootEl.querySelector<SVGSVGElement>('.SectionHeader__title > svg');
            const textEl = svgEl?.querySelector<SVGTextElement>('text');
            if (!svgEl || !textEl) return;

            try {
                const bbox = textEl.getBBox();
                rootEl.style.setProperty('--strategy-title-svg-width', `${bbox.width}px`);

                const textCenter = bbox.y + bbox.height / 2;
                rootEl.style.setProperty('--strategy-title-extra-offset-y', `${textCenter - SVG_TITLE_HEIGHT_PX / 2}px`);
            } catch {
                // getBBox is unimplemented in jsdom
            }
        };

        measureTitleWidth();
        window.addEventListener('resize', measureTitleWidth);

        return () => window.removeEventListener('resize', measureTitleWidth);
    }, [isTitleCompact]);

    const headerActionByState: Record<StrategyDisplayStateEnum, JSX.Element | null> = {
        [StrategyDisplayStateEnum.Empty]: null,
        [StrategyDisplayStateEnum.Generating]: null,
        [StrategyDisplayStateEnum.Loading]: null,
        [StrategyDisplayStateEnum.Ready]: isFormOpen ? null : (
            <div className="Strategy__header-actions">
                {(isExpanded || !canExpand) && (
                    <>
                        <Button className="appears" modifiers={["primary"]} onClick={() => setIsFormOpen(true)}>{SSB_RESTART}</Button>
                        <StrategyCopyButton className="appears" sections={sections} userInput={userInput} />
                        {canExpand && <span className="Strategy__header-divider appears" />}

                    </>
                )}
                {canExpand && (
                    <StrategyViewToggle isExpanded={isExpanded} onToggle={() => setIsExpanded(prev => !prev)} />
                )}
            </div>
        ),
    };

    const bodyByState: Record<StrategyDisplayStateEnum, JSX.Element | null> = {
        [StrategyDisplayStateEnum.Empty]: (
            <StrategyGetStartedForm initialValue={userInput} onSubmit={handleSubmit} isSubmitting={generation.isGenerating} />
        ),
        [StrategyDisplayStateEnum.Generating]: (
            <div className="Strategy__generating">
                <Loader className="is-no-bg" />
                <span className="Strategy__generating-text">{SSB_GENERATING_TEXT}</span>
            </div>
        ),
        [StrategyDisplayStateEnum.Loading]: (
            <div className="Strategy__generating">
                <Loader className="is-no-bg" />
            </div>
        ),
        [StrategyDisplayStateEnum.Ready]: isFormOpen ? (
            <StrategyGetStartedForm initialValue={userInput} onSubmit={handleSubmit} isSubmitting={generation.isGenerating} />
        ) : (
            <>
                <StrategyContent sections={sections} isExpanded={isExpanded} userInput={userInput} />
                {isStrategyIncomplete(state, isPartial) && (
                    <div className="Strategy__partial-loader">
                        <Loader isSimple />
                        <span className="Strategy__partial-loader-text">{SSB_ALMOST_THERE_TEXT}</span>
                    </div>
                )}
            </>
        ),
    };

    const body = bodyByState[displayState];

    const isCollapsedWithNothingToShow =
        displayState === StrategyDisplayStateEnum.Ready && !isFormOpen && !isExpanded && !isStrategyIncomplete(state, isPartial);
    const showCard = !isCollapsedWithNothingToShow && Boolean(body || generation.errorMessage);

    return (
        <div
            className={cn(
                "Strategy",
                isHeaderCompact && "Strategy--compact",
                isTitleCompact && "Strategy--title-compact",
                !showCard && "Strategy--collapsed",
            )}
            ref={rootRef}
        >
            <SectionHeader title={SSB_TITLE} titleExtra={<StrategyTitleInfo createdAt={generation.isGenerating ? null : createdAt} />}>
                {headerActionByState[displayState]}
            </SectionHeader>
            {showCard && (
                <div className="Strategy__card">
                    {body}
                    {generation.errorMessage && <div className="Strategy__error">{generation.errorMessage}</div>}
                </div>
            )}
            <CornerLabel modifiers={["top", "right"]}>{SSB_BETA_LABEL}</CornerLabel>
        </div>
    );
};

export default Strategy;
